/**
 * PilotOfficersPage — System · Pilot Officers.
 *
 * PILOT-GATE-INFRA (Bill 2026-04-25 binding pilot-gating rule). Two-layer gate
 * (server: Services/PilotGateService.cs):
 *   Layer 1 — `pilot.mode` SystemSettings toggle (default 'true' at v3.0).
 *   Layer 2 — explicit (OfficerCode × FeatureCode) opt-in rows in
 *             dbo.PilotOfficers. The matrix below is that table.
 *
 * During pilot, only enrolled officers see Weekly Tasks / Retail QC / Fraud
 * Cases / Anniversary Nurture / Cross-Sell / Engagement Timeline. Unenrolled
 * officers continue legacy processes until full cutover.
 *
 * Contract (verified 2026-06-16 against primary sources — no drift):
 *   - Entity:  PCBJxChangeApi/Data/Entities/Config/PilotOfficer.cs
 *   - Read:    GET    /v3/admin/pilot-officers
 *                 → { success, data:[{officerCode,featureCode,isActive,
 *                       enrolledAt,enrolledBy,notes,createdAt,updatedAt,updatedBy}] }
 *   - Read:    GET    /v3/config/users  (officer roster — isOfficer/officerCode/isActive)
 *   - Read:    GET    /v3/config/system-settings  (flat { 'pilot.mode':'true', … })
 *   - Write:   POST   /v3/admin/pilot-officers/upsert
 *   - Delete:  DELETE /v3/admin/pilot-officers/{officerCode}/{featureCode}  (soft-delete, IsActive=false)
 *   - Bulk:    POST   /v3/admin/pilot-officers/bulk-enroll
 *   - Promote: POST   /v3/admin/system-settings  (flips pilot.mode='false')
 *   - DTOs:    PilotOfficerUpsertInput / PilotOfficerBulkEnrollInput /
 *              SystemSettingUpsertBody (api-client) — every field persisted by
 *              AdminPilotOfficersFunction.cs / AdminSystemFunction.cs.
 *
 * Wire field names line up across entity → function → shared-types →
 * api-client (officerCode/featureCode/isActive/enrolledAt/enrolledBy). The
 * typed `pilotOfficers.list()` returns `ApiResult<PilotOfficer[]>`, matching
 * the server envelope exactly — no raw-client workaround needed.
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle, no
 * bypass-key prompt). The ROP-teams source used promptBypassKey(); the
 * admin-panel posture is MSAL Path B.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN_AUTH_TOKEN_SENTINEL, ApiError } from '@retail-ops/api-client';
import type {
  PilotFeatureCode,
  PilotOfficer,
  PilotOfficerBulkEnrollInput,
} from '@retail-ops/shared-types';
import { PILOT_FEATURE_CODES } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { ModalShell } from '@/components/ModalShell';

// ── Constants ────────────────────────────────────────────────────────────

const FEATURE_LABELS: Record<PilotFeatureCode, string> = {
  'weekly-tasks': 'Weekly Tasks',
  'retail-qc': 'Retail QC',
  'fraud-cases': 'Fraud Cases',
  'anniversary-nurture': 'Anniversary Nurture',
  'cross-sell': 'Cross-Sell',
  'engagement-timeline': 'Engagement Timeline',
};

const FEATURE_HELP: Record<PilotFeatureCode, string> = {
  'weekly-tasks': 'Pilot officers see Monday weekly-task generation; everyone else continues legacy queues.',
  'retail-qc': "QC items only appear in pilot officers' Retail QC inbox.",
  'fraud-cases': 'Fraud-case auto-assignment only routes to enrolled officers.',
  'anniversary-nurture': "Annual customer touch-points only fire for enrolled officers' books.",
  'cross-sell': "Cross-sell prompts surface only on enrolled officers' carts.",
  'engagement-timeline': "Customer-engagement timeline rows are written only for enrolled officers' interactions.",
};

const MIN_OFFICERS_FOR_PROMOTION = 3;

// ── Local row shapes ───────────────────────────────────────────────────────

/** Mirrors the /v3/config/users wire (V3ConfigFunction.cs:1141). */
interface OfficerRow {
  userId: number;
  email: string;
  displayName: string;
  officerCode: string | null;
  isOfficer: boolean;
  isActive: boolean;
}

// ── Page ───────────────────────────────────────────────────────────────────

export function PilotOfficersPage(): JSX.Element {
  const client = useApiClient();
  const [officers, setOfficers] = useState<ReadonlyArray<OfficerRow>>([]);
  const [enrollments, setEnrollments] = useState<ReadonlyArray<PilotOfficer>>([]);
  const [pilotMode, setPilotMode] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [pendingCells, setPendingCells] = useState<ReadonlySet<string>>(new Set());
  const [showPromote, setShowPromote] = useState<boolean>(false);
  const [promoteStep, setPromoteStep] = useState<1 | 2 | 3>(1);
  const [promoting, setPromoting] = useState<boolean>(false);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      const [usersResp, pilotResp, settingsResp] = await Promise.all([
        client.http.get<{ success: boolean; data?: ReadonlyArray<OfficerRow>; error?: string }>(
          '/v3/config/users',
        ),
        client.retail.admin.pilotOfficers.list(),
        client.retail.config.systemSettings(),
      ]);

      if (usersResp.success && usersResp.data) {
        // Only show active officers — matches the gate's "responsible officer"
        // semantics; an enrolment row keys on OfficerCode.
        setOfficers(
          usersResp.data
            .filter((u) => u.isOfficer && u.isActive && !!u.officerCode && u.officerCode.trim().length > 0)
            .slice()
            .sort((a, b) => (a.officerCode ?? '').localeCompare(b.officerCode ?? '')),
        );
      } else {
        setLoadError(usersResp.error ?? 'Failed to load officers.');
      }

      // Typed list() returns ApiResult<PilotOfficer[]> — { success, data }.
      if (pilotResp.success && pilotResp.data) {
        setEnrollments(pilotResp.data);
      } else if (pilotResp.success === false) {
        setLoadError(pilotResp.error ?? 'Failed to load pilot enrolments.');
      } else {
        setEnrollments(pilotResp.data ?? []);
      }

      // /v3/config/system-settings returns an ARRAY of SystemSetting rows
      // ({ settingKey, settingValue, … }), NOT a flat dict — indexing it by
      // 'pilot.mode' always yielded undefined, so the toggle/pill/promote-gate
      // were stuck reporting ON regardless of the DB (BACKLOG-0579 verify fix
      // 2026-06-17; matches RcpScaffoldPage's read).
      let parsed = true; // default-true matches the server-side fallback
      if (Array.isArray(settingsResp.data)) {
        const row = (settingsResp.data as unknown as ReadonlyArray<{ settingKey?: string; settingValue?: string | null }>)
          .find((r) => r?.settingKey === 'pilot.mode');
        const raw = row?.settingValue;
        if (typeof raw === 'string') parsed = raw.trim().toLowerCase() === 'true';
      }
      setPilotMode(parsed);
    } catch (e: unknown) {
      setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // (officer × feature) → enrolment row lookup
  const enrollmentMap = useMemo(() => {
    const m = new Map<string, PilotOfficer>();
    for (const e of enrollments) m.set(`${e.officerCode}::${e.featureCode}`, e);
    return m;
  }, [enrollments]);

  /** Per-feature count of *active* officers — drives the Promote gating. */
  const featureCounts = useMemo(() => {
    const counts: Record<PilotFeatureCode, number> = {
      'weekly-tasks': 0,
      'retail-qc': 0,
      'fraud-cases': 0,
      'anniversary-nurture': 0,
      'cross-sell': 0,
      'engagement-timeline': 0,
    };
    for (const e of enrollments) {
      if (e.isActive) counts[e.featureCode] = (counts[e.featureCode] ?? 0) + 1;
    }
    return counts;
  }, [enrollments]);

  const promoteEligible = useMemo(
    () => PILOT_FEATURE_CODES.every((f) => (featureCounts[f] ?? 0) >= MIN_OFFICERS_FOR_PROMOTION),
    [featureCounts],
  );

  // ── Cell toggle (single (officer, feature)) ──────────────────────────────

  const toggleCell = useCallback(
    async (officerCode: string, featureCode: PilotFeatureCode, nextActive: boolean): Promise<void> => {
      const cellKey = `${officerCode}::${featureCode}`;
      setPendingCells((prev) => new Set(prev).add(cellKey));

      // Optimistic update
      const prevSnapshot = enrollments;
      setEnrollments((prev) => {
        const idx = prev.findIndex((e) => e.officerCode === officerCode && e.featureCode === featureCode);
        if (idx >= 0) {
          const copy = prev.slice();
          copy[idx] = { ...copy[idx], isActive: nextActive };
          return copy;
        }
        return [
          ...prev,
          { officerCode, featureCode, isActive: nextActive, enrolledAt: new Date().toISOString() },
        ];
      });

      try {
        if (nextActive) {
          const resp = await client.retail.admin.pilotOfficers.upsert(ADMIN_AUTH_TOKEN_SENTINEL, {
            officerCode,
            featureCode,
            isActive: true,
          });
          if (!resp.success) throw new Error(resp.error ?? 'upsert returned success=false');
        } else {
          const resp = await client.retail.admin.pilotOfficers.remove(
            ADMIN_AUTH_TOKEN_SENTINEL,
            officerCode,
            featureCode,
          );
          if (!resp.success) throw new Error(resp.error ?? 'remove returned success=false');
        }
        setToast({
          tone: 'info',
          text: `${nextActive ? 'Enrolled' : 'Removed'} ${officerCode} → ${FEATURE_LABELS[featureCode]}.`,
        });
      } catch (e: unknown) {
        setEnrollments(prevSnapshot); // rollback
        const msg = ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e);
        setToast({ tone: 'warn', text: `Save failed: ${msg}` });
      } finally {
        setPendingCells((prev) => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
      }
    },
    [client, enrollments],
  );

  // ── Bulk actions ─────────────────────────────────────────────────────────

  const bulkEnroll = useCallback(
    async (input: PilotOfficerBulkEnrollInput, label: string): Promise<void> => {
      try {
        const resp = await client.retail.admin.pilotOfficers.bulkEnroll(ADMIN_AUTH_TOKEN_SENTINEL, input);
        if (!resp.success) throw new Error(resp.error ?? 'bulk-enroll returned success=false');
        await refresh();
        const data = (resp.data ?? {}) as { created?: number; updated?: number; totalPairs?: number };
        setToast({
          tone: 'info',
          text: `${label}: ${data.created ?? 0} added / ${data.updated ?? 0} re-activated (${data.totalPairs ?? 0} pairs).`,
        });
      } catch (e: unknown) {
        const msg = ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e);
        setToast({ tone: 'warn', text: `Bulk enrol failed: ${msg}` });
      }
    },
    [client, refresh],
  );

  const enrollAllOfficersIn = useCallback(
    (featureCode: PilotFeatureCode): void => {
      const officerCodes = officers
        .map((o) => o.officerCode)
        .filter((c): c is string => !!c && c.trim().length > 0);
      if (officerCodes.length === 0) {
        setToast({ tone: 'warn', text: 'No active officers to enrol.' });
        return;
      }
      void bulkEnroll(
        { officerCodes, featureCode },
        `Enrolled all officers in ${FEATURE_LABELS[featureCode]}`,
      );
    },
    [officers, bulkEnroll],
  );

  const enrollOfficerInAll = useCallback(
    (officerCode: string): void => {
      void bulkEnroll(
        { officerCode, featureCodes: PILOT_FEATURE_CODES },
        `Enrolled ${officerCode} in every feature`,
      );
    },
    [bulkEnroll],
  );

  // ── pilot.mode toggle (writes through SystemSettings admin route) ────────

  const flipPilotMode = useCallback(
    async (next: boolean): Promise<void> => {
      try {
        const resp = await client.retail.admin.systemSettings.upsert(ADMIN_AUTH_TOKEN_SENTINEL, {
          settingKey: 'pilot.mode',
          settingValue: next ? 'true' : 'false',
          dataType: 'bool',
          categoryCode: 'pilot',
          isActive: true,
        });
        if (!resp.success) throw new Error(resp.error ?? 'systemSettings upsert returned success=false');
        setPilotMode(next);
        setToast({
          tone: next ? 'info' : 'warn',
          text: next
            ? 'Pilot mode is ON. Only enrolled officers see Weekly Tasks / QC / Fraud / nurture campaigns.'
            : 'Pilot mode is OFF. Every officer now sees every feature — full production.',
        });
      } catch (e: unknown) {
        const msg = ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e);
        setToast({ tone: 'warn', text: `Could not change pilot.mode: ${msg}` });
      }
    },
    [client],
  );

  const handlePromote = useCallback(async (): Promise<void> => {
    setPromoting(true);
    try {
      await flipPilotMode(false);
      setShowPromote(false);
      setPromoteStep(1);
    } finally {
      setPromoting(false);
    }
  }, [flipPilotMode]);

  // ── Matrix columns (officer + one toggle column per feature + bulk) ──────
  // DataTableColumn.header is typed `string` (shared primitive — not editable
  // here), so the per-feature enrolled-count Pill + "Enroll all this feature"
  // control lives in the feature-controls strip above the table instead of in
  // a JSX header. The toggle grid itself stays in the DataTable cells.

  const columns: ReadonlyArray<DataTableColumn<OfficerRow>> = useMemo(() => {
    const officerCol: DataTableColumn<OfficerRow> = {
      header: 'Officer',
      cell: (o) => (
        <div>
          <code className="cell-mono--bold">{o.officerCode}</code>
          <div className="cell-desc">{o.displayName}</div>
        </div>
      ),
    };

    const featureCols: ReadonlyArray<DataTableColumn<OfficerRow>> = PILOT_FEATURE_CODES.map((f) => ({
      header: FEATURE_LABELS[f],
      align: 'center' as const,
      cell: (o) => {
        const code = o.officerCode!;
        const cellKey = `${code}::${f}`;
        const row = enrollmentMap.get(cellKey);
        const isActive = !!row?.isActive;
        const isPending = pendingCells.has(cellKey);
        return (
          <label
            className="form-bool"
            title={
              row?.enrolledAt
                ? `Enrolled ${row.enrolledAt}${row.enrolledBy ? ` by ${row.enrolledBy}` : ''}`
                : 'Not enrolled'
            }
          >
            <input
              type="checkbox"
              aria-label={`${code} enrolled in ${FEATURE_LABELS[f]}`}
              checked={isActive}
              disabled={isPending}
              onChange={(e) => void toggleCell(code, f, e.target.checked)}
            />
          </label>
        );
      },
    }));

    const bulkCol: DataTableColumn<OfficerRow> = {
      header: 'Enroll in all',
      align: 'center' as const,
      cell: (o) => (
        <Button size="sm" variant="ghost" onClick={() => enrollOfficerInAll(o.officerCode!)}>
          Enroll all
        </Button>
      ),
    };

    return [officerCol, ...featureCols, bulkCol];
  }, [enrollmentMap, pendingCells, toggleCell, enrollOfficerInAll]);

  // ── Render ───────────────────────────────────────────────────────────────

  if (loadError && officers.length === 0) {
    return (
      <Banner tone="err">
        Could not load pilot officers: {loadError} <Button onClick={() => void refresh()}>Retry</Button>
      </Banner>
    );
  }
  if (loading) return <Spinner label="Loading officer matrix…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}
      {loadError && <Banner tone="warn">{loadError}</Banner>}

      {/* pilot.mode toggle */}
      <Banner tone={pilotMode ? 'warn' : 'info'}>
        <div className="card-actions-row">
          <strong>pilot.mode</strong>
          <label className="form-bool">
            <input
              type="checkbox"
              aria-label="Toggle pilot.mode"
              checked={pilotMode === true}
              disabled={pilotMode === null}
              onChange={(e) => void flipPilotMode(e.target.checked)}
            />
            <span>{pilotMode ? 'On — only enrolled officers see pilot features' : 'Off — full production'}</span>
          </label>
          <span className="cell-desc">
            Setting pilot.mode = false enables ALL features for ALL officers immediately. Use only when you&apos;re
            ready for full cutover.
          </span>
        </div>
      </Banner>

      <Card
        title={
          <span>
            Pilot Officers — {officers.length} officer{officers.length === 1 ? '' : 's'}{' '}
            {pilotMode ? <Pill tone="warn">Pilot mode</Pill> : <Pill tone="ok">Full production</Pill>}
          </span>
        }
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setShowPromote(true);
              setPromoteStep(1);
            }}
            disabled={!promoteEligible || pilotMode === false}
          >
            Promote to full production
          </Button>
        }
      >
        <p className="cell-desc">
          During pilot, only enrolled officers see Weekly Tasks, Retail QC, Fraud Cases, Anniversary Nurture,
          Cross-Sell, and the Engagement Timeline. Unenrolled officers continue their legacy processes until full
          cutover. <em>Promote to full production</em> unlocks once at least {MIN_OFFICERS_FOR_PROMOTION} officers
          are enrolled in every feature.
        </p>

        {/* Per-feature controls — count + "enroll every officer in this feature". */}
        <div className="pill-stack">
          {PILOT_FEATURE_CODES.map((f) => (
            <span key={f} className="card-actions-row" title={FEATURE_HELP[f]}>
              <Pill tone={featureCounts[f] >= MIN_OFFICERS_FOR_PROMOTION ? 'ok' : 'muted'}>
                {FEATURE_LABELS[f]}: {featureCounts[f]}
              </Pill>
              <Button size="sm" variant="ghost" onClick={() => enrollAllOfficersIn(f)}>
                Enroll all
              </Button>
            </span>
          ))}
        </div>

        <DataTable<OfficerRow>
          columns={columns}
          rows={officers}
          rowKey={(o) => String(o.userId)}
          emptyState={
            <p className="empty">
              No active officers found. Add officers from <strong>Identity → Users</strong> first.
            </p>
          }
        />
      </Card>

      {/* ── Promote modal — 3-step confirmation ─────────────────────────── */}
      <ModalShell
        open={showPromote}
        title={`Promote to full production — step ${promoteStep} of 3`}
        onClose={() => (promoting ? undefined : setShowPromote(false))}
        width={560}
        actions={
          <>
            <Button onClick={() => setShowPromote(false)} disabled={promoting}>
              Cancel
            </Button>
            {promoteStep < 3 ? (
              <Button
                variant="primary"
                onClick={() => setPromoteStep((promoteStep + 1) as 1 | 2 | 3)}
                disabled={promoting}
              >
                Continue
              </Button>
            ) : (
              <Button variant="primary" onClick={() => void handlePromote()} disabled={promoting}>
                {promoting ? 'Flipping pilot.mode…' : 'Yes, flip to full production'}
              </Button>
            )}
          </>
        }
      >
        {promoteStep === 1 && (
          <div className="form-stack">
            <p className="modal-prose">
              You&apos;re about to flip <code className="cell-mono">pilot.mode</code> to <strong>false</strong>.
              Once you do:
            </p>
            <ul className="modal-prose">
              <li>Every officer immediately sees every pilot feature.</li>
              <li>Timer-trigger generators stop short-circuiting.</li>
              <li>Legacy fall-back paths for unenrolled officers shut off.</li>
            </ul>
            <p className="modal-prose">This is the ROP cutover. There&apos;s no scheduled rollback.</p>
          </div>
        )}
        {promoteStep === 2 && (
          <div className="form-stack">
            <p className="modal-prose">Confirm pilot stats look ready:</p>
            <ul className="modal-prose">
              {PILOT_FEATURE_CODES.map((f) => (
                <li key={f}>
                  <strong>{FEATURE_LABELS[f]}</strong> — {featureCounts[f]} officers enrolled
                </li>
              ))}
            </ul>
            <p className="modal-prose">If any of these look low, cancel and finish onboarding before promoting.</p>
          </div>
        )}
        {promoteStep === 3 && (
          <div className="form-stack">
            <p className="modal-prose">
              <strong>Final confirmation.</strong> Clicking <em>Yes, flip to full production</em> writes{' '}
              <code className="cell-mono">pilot.mode = false</code> via the SystemSettings admin route. The change
              takes effect within the next 30 seconds (gate cache TTL).
            </p>
            <p className="modal-prose">
              Pilot enrolment rows are <strong>preserved</strong> for audit — they just stop gating anything once
              pilot.mode is off.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}

export default PilotOfficersPage;
